import type { WorkflowCardFieldDisplayOrderItem } from '../api/workflow-card-field-display-order-api.service';
import type { CustomFieldViewModel } from '../../custom-fields/models/custom-field.model';
import type { WorkflowQuickField } from '../models/workflow-item.model';

/** Ids de campos personalizados na sequência definida pelo modal / API (só `kind === 'custom'`). */
export function orderedCustomWorkflowFieldIdsFromDisplayOrder(
  items: WorkflowCardFieldDisplayOrderItem[]
): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const it of items) {
    if (it.kind !== 'custom') continue;
    const id = it.workflowFieldId?.trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

/** Ordena quick fields do item (pills lista/board) pela ordem dos personalizados na configuração do card. */
export function sortWorkflowQuickFieldsByDisplayOrder(
  quickFields: WorkflowQuickField[],
  orderItems: WorkflowCardFieldDisplayOrderItem[] | null | undefined
): WorkflowQuickField[] {
  if (!orderItems?.length || quickFields.length === 0) return quickFields;
  const orderIds = orderedCustomWorkflowFieldIdsFromDisplayOrder(orderItems);
  if (orderIds.length === 0) return quickFields;
  const idx = new Map<string, number>();
  orderIds.forEach((id, i) => idx.set(id.trim().toLowerCase(), i));
  return [...quickFields]
    .map((f, originalIndex) => ({ f, originalIndex }))
    .sort((x, y) => {
      const ia = idx.get(x.f.workflowFieldId.trim().toLowerCase());
      const ib = idx.get(y.f.workflowFieldId.trim().toLowerCase());
      const va = ia ?? 10_000 + x.originalIndex;
      const vb = ib ?? 10_000 + y.originalIndex;
      return va - vb;
    })
    .map((x) => x.f);
}

/**
 * Ordena definições de campos personalizados para formulário / painel do card:
 * mesma sequência que os itens `custom` em card-field-display-order.
 * Sem ordem da API, mantém ordenação por `order` do schema.
 */
export function sortCustomFieldViewModelsByDisplayOrder(
  fields: CustomFieldViewModel[],
  orderItems: WorkflowCardFieldDisplayOrderItem[] | null | undefined
): CustomFieldViewModel[] {
  if (!orderItems?.length) {
    return [...fields].sort((a, b) => a.order - b.order);
  }
  const orderIds = orderedCustomWorkflowFieldIdsFromDisplayOrder(orderItems);
  if (orderIds.length === 0) {
    return [...fields].sort((a, b) => a.order - b.order);
  }
  const idx = new Map<string, number>();
  orderIds.forEach((id, i) => idx.set(id.trim().toLowerCase(), i));
  return [...fields]
    .map((f, originalIndex) => ({ f, originalIndex }))
    .sort((x, y) => {
      const ia = idx.get(x.f.fieldId.trim().toLowerCase());
      const ib = idx.get(y.f.fieldId.trim().toLowerCase());
      const va = ia ?? 10_000 + x.originalIndex;
      const vb = ib ?? 10_000 + y.originalIndex;
      return va - vb;
    })
    .map((x) => x.f);
}
